import { isRejectedClerkSession } from '../../services/authGate';
import {
  setChosenCompany, clearChosenCompany, getChosenCompany,
  onCompanyAccess, logClientDecision, setDecisionSink, COMPANY_NOTICE_STORAGE_KEY, type CompanyAccessKind,
} from '../../services/portalRequest';
import { logEvent } from '../../services/portalLogService';
import { usePermissions, pendingInviteTarget } from '../../hooks/usePermissions';
import { consumeReturnPath } from '../../services/authGate';
/**
 * AuthenticatedLayout - Layout wrapper for authenticated pages
 *
 * Version History:
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01): role, permissions and the companies list of the CHOSEN company come
 *                      from the shared snapshot (hooks/usePermissions), filled once from /api/me. 403 not_a_member clears the choice and
 *                      returns the person to their own company with a message; 409 company_choice_required shows the
 *                      choice. Every decision is logged with the company id and no email.
 * v4.30 - 2026-10-15 - ROW 69 (ruling 10): outcome 'setup_incomplete' shows one translated message (account setup is incomplete, contact support); the decision is logged with the company id
 * v2.95 - 2026-03-13 - Trials purged: removed TrialBanner import and render
 * v2.93 - 2026-02-07 - Mobile responsive: temporary drawer + hamburger toggle below md breakpoint
 * v2.77 - 2026-02-05 - Fixed fetch('/api/me') to include X-Authorization header for Clerk JWT
 * v2.55 - 2026-02-04 - Migrated to Clerk authentication (useAuth, useUser)
 * v2.33 - 2026-01-31 - Added role to auth state for permission-based sidebar filtering
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, List, ListItemButton,
  ListItemText, useMediaQuery, useTheme,
} from '@mui/material';
import TopBar, { TOPBAR_HEIGHT } from './TopBar';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';
import ChatWidget from '../ChatWidget'; // v3.60 - NLP Chat floating widget - 2026-03-03

const AuthenticatedLayout: React.FC = () => {
  const { t } = useTranslation();
  // v4.28 - G10-B: /api/me is read ONCE, by the app's identity check (App.tsx useClerkAuth -> applyMeResponse); the layout
  // reads the result from the shared store. Role and permissions are those of the CHOSEN company.
  const { isSignedIn, isLoaded: authLoaded, sessionId } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { outcome, loaded, choiceCompanies, pendingInvite } = usePermissions();
  const navigate = useNavigate();
  const isLoading = !authLoaded || !userLoaded;
  const isAuthenticated = !!isSignedIn && !!user && !isRejectedClerkSession(sessionId);
  const retry = () => window.location.reload();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false); // v2.93 - Mobile drawer state
  const [notice, setNotice] = useState<string | null>(null);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md')); // v2.93

  // v4.28 - G10-B: client decisions are shipped to the portal log (best effort; never an email)
  useEffect(() => {
    setDecisionSink((event, fields) => { void logEvent(`company_${event}`, fields); });
    return () => setDecisionSink(null);
  }, []);

  // v4.28 - G10-B: the message left by the last refusal (shown once)
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(COMPANY_NOTICE_STORAGE_KEY);
      if (stored) {
        sessionStorage.removeItem(COMPANY_NOTICE_STORAGE_KEY);
        setNotice(stored);
      }
    } catch { /* optional */ }
  }, []);

  // v4.28 - G10-B: a refused company. not_a_member: clear the choice and go back to the person's own company.
  const handleAccess = useCallback((kind: CompanyAccessKind) => {
    if (kind === 'unavailable') { setNotice('unavailable'); return; } // 503: one translated "try again" message
    if (kind === 'setup_incomplete') { setNotice('setup_incomplete'); return; } // v4.30 - ROW 69: 409 tenant_state_incomplete
    if (kind !== 'not_a_member') return; // company_choice_required is rendered by outcome below
    const refused = getChosenCompany();
    if (!refused) return;
    logClientDecision('not_a_member_choice_cleared', { company: refused });
    clearChosenCompany();
    try { sessionStorage.setItem(COMPANY_NOTICE_STORAGE_KEY, 'not_a_member'); } catch { /* optional */ }
    window.location.assign('/dashboard');
  }, []);
  useEffect(() => onCompanyAccess(handleAccess), [handleAccess]);

  // v4.28 - G10-B (final G10-A contract, gap 4): a signed-in person with no company and a pending team invite goes to that
  // invite's accept page. The server sends acceptPath only when the signed-in verified email equals the invite's email; then
  // that is the target. Without it the page is the one the person opened from the email (remembered for this tab); with
  // neither the person is told where to find the link. The decision logs the inviteId only, never the path.
  const [inviteRedirected, setInviteRedirected] = useState(false);
  useEffect(() => {
    if (outcome !== 'pending_invite' || inviteRedirected) return;
    const inviteId = pendingInvite ? pendingInvite.inviteId : 'none';
    const target = pendingInviteTarget(pendingInvite, consumeReturnPath());
    if (target) {
      logClientDecision(pendingInvite && pendingInvite.acceptPath ? 'pending_invite_redirect_accept_path' : 'pending_invite_redirect', { invite: inviteId });
      setInviteRedirected(true);
      navigate(target, { replace: true });
    } else {
      logClientDecision('pending_invite_notice_shown', { invite: inviteId });
    }
  }, [outcome, inviteRedirected, pendingInvite, navigate]);

  // Show loading during auth check
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // v2.55 - Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // v4.28 - G10-B: 409 company_choice_required - the person chooses; nothing is guessed
  if (outcome === 'choice_required') {
    return (
      <Dialog open fullWidth maxWidth="xs">
        <DialogTitle>{t('teamAccess.choice.title')}</DialogTitle>
        <DialogContent>
          {choiceCompanies.length === 0 ? (
            <Alert severity="info">{t('teamAccess.choice.noneListed')}</Alert>
          ) : (
            <List>
              {choiceCompanies.map((company) => (
                <ListItemButton
                  key={company.tenantUuid}
                  onClick={() => {
                    setChosenCompany(company.tenantUuid, user ? user.id : null);
                    logClientDecision('company_chosen_from_dialog', { company: company.tenantUuid });
                    window.location.assign('/dashboard');
                  }}
                >
                  <ListItemText primary={company.companyName || t('teamAccess.company.unnamed')} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // v4.28 - G10-B: pending team invite and no remembered link: say where the invitation is
  if (outcome === 'pending_invite') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">
          {pendingInvite && pendingInvite.companyName
            ? t('teamAccess.pendingInvite.messageWithCompany', { company: pendingInvite.companyName })
            : t('teamAccess.pendingInvite.message')}
        </Alert>
      </Box>
    );
  }

  // v4.30 - ROW 69 (ruling 10): the company record has no workflow state - say so, with no retry loop
  if (outcome === 'setup_incomplete') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          {t('teamAccess.setupIncomplete')}
        </Alert>
      </Box>
    );
  }

  // v4.28 - G10-B: a company lookup is temporarily unavailable (503): one "try again" message
  if (outcome === 'unavailable') {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={retry}>{t('teamAccess.loadFailed.retry')}</Button>}>
          {t('teamAccess.unavailable')}
        </Alert>
      </Box>
    );
  }

  // v4.28 - G10-B: access information could not be loaded - say so, with a retry (no redirect loop)
  if (outcome === 'failed' || (outcome === 'ok' && !loaded)) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={retry}>{t('teamAccess.loadFailed.retry')}</Button>}>
          {t('teamAccess.loadFailed.message')}
        </Alert>
      </Box>
    );
  }

  // v2.93 - On mobile, sidebar is temporary (no margin needed)
  const currentSidebarWidth = isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <TopBar onMenuToggle={isMobile ? () => setMobileOpen(!mobileOpen) : undefined} />
      <Sidebar
        collapsed={isMobile ? false : sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isMobile={isMobile}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${currentSidebarWidth}px`,
          mt: `${TOPBAR_HEIGHT}px`,
          p: { xs: 2, sm: 3 }, // v2.93 - Smaller padding on mobile
          bgcolor: 'background.default',
          minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
          transition: 'margin-left 0.2s ease-in-out',
        }}
      >
        {notice === 'unavailable' && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {t('teamAccess.unavailable')}
          </Alert>
        )}
        {notice === 'setup_incomplete' && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {t('teamAccess.setupIncomplete')}
          </Alert>
        )}
        {notice === 'not_a_member' && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {t('teamAccess.notice.notAMember')}
          </Alert>
        )}
        <Outlet />
      </Box>
      {/* v3.60 - NLP Chat widget: floating on all authenticated pages */}
      <ChatWidget />
    </Box>
  );
};

export default AuthenticatedLayout;
